/**
 * Hash routes (#/booth/45/identity). Hash routing needs no server rewrite rules, so the app works
 * from any static host or CDN folder, and the service worker only ever serves index.html.
 */
import { useEffect, useState } from "react";
import { FIELD_SECTIONS, LIST_SECTIONS, type ListSection } from "@npac/rules";
import type { FieldSection } from "@core/types";

export type CampaignScreen = "rallies" | "tour" | "vehicles";

export type Route =
  /** होम: one icon per section of this login's work */
  | { name: "home" }
  | { name: "booths" }
  | { name: "booth"; code: string }
  | { name: "section"; code: string; section: FieldSection }
  | { name: "location"; code: string }
  | { name: "people"; code: string; list: ListSection }
  | { name: "sentiment"; code: string }
  | { name: "result"; code: string }
  | { name: "places"; code: string }
  /** विधानसभा की सभी जगहें: every booth's places (the places duty) */
  | { name: "allplaces" }
  | { name: "campaign" }
  | { name: "camp"; part: CampaignScreen }
  | { name: "polling" }
  | { name: "exitpoll" }
  /** वोट डाला: polling-day booth agent (the "voted" duty) */
  | { name: "voted" }
  /** मतगणना: counting agent (the "counting" duty) */
  | { name: "counting" }
  /** वर्तमान सर्वे: door-to-door / telecaller survey (the "d2d" / "tele" duties) */
  | { name: "survey" }
  | { name: "attendance" }
  | { name: "review" }
  | { name: "sync" }
  | { name: "assembly" };

export function parseRoute(hash: string): Route {
  const parts = hash.replace(/^#\/?/, "").split("/").filter(Boolean).map(decodeURIComponent);
  const [a, b, c] = parts;
  if (a === "booth" && b) {
    if (c === "location") return { name: "location", code: b };
    if (c === "sentiment") return { name: "sentiment", code: b };
    if (c === "result") return { name: "result", code: b };
    if (c === "places") return { name: "places", code: b };
    if (c && (LIST_SECTIONS as readonly string[]).includes(c)) return { name: "people", code: b, list: c as ListSection };
    if (c && (FIELD_SECTIONS as readonly string[]).includes(c)) return { name: "section", code: b, section: c as FieldSection };
    return { name: "booth", code: b };
  }
  if (a === "booths") return { name: "booths" };
  if (a === "polling") return { name: "polling" };
  if (a === "places") return { name: "allplaces" };
  if (a === "exitpoll") return { name: "exitpoll" };
  if (a === "voted") return { name: "voted" };
  if (a === "counting") return { name: "counting" };
  if (a === "survey") return { name: "survey" };
  if (a === "attendance") return { name: "attendance" };
  if (a === "review") return { name: "review" };
  if (a === "campaign") return b === "rallies" || b === "tour" || b === "vehicles" ? { name: "camp", part: b } : { name: "campaign" };
  if (a === "sync") return { name: "sync" };
  if (a === "assembly") return { name: "assembly" };
  return { name: "home" };
}

export function href(r: Route): string {
  switch (r.name) {
    case "home":
      return "#/";
    case "booths":
      return "#/booths";
    case "booth":
      return `#/booth/${encodeURIComponent(r.code)}`;
    case "section":
      return `#/booth/${encodeURIComponent(r.code)}/${r.section}`;
    case "location":
      return `#/booth/${encodeURIComponent(r.code)}/location`;
    case "people":
      return `#/booth/${encodeURIComponent(r.code)}/${r.list}`;
    case "sentiment":
    case "result":
    case "places":
      return `#/booth/${encodeURIComponent(r.code)}/${r.name}`;
    case "camp":
      return `#/campaign/${r.part}`;
    case "allplaces":
      return "#/places";
    default:
      return `#/${r.name}`;
  }
}

export function navigate(r: Route, replace = false) {
  const h = href(r);
  if (replace) history.replaceState(null, "", h);
  else location.hash = h;
  window.dispatchEvent(new HashChangeEvent("hashchange"));
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => parseRoute(location.hash));
  useEffect(() => {
    const on = () => {
      setRoute(parseRoute(location.hash));
      window.scrollTo(0, 0);
    };
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return route;
}
